import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { fetchUnreadCount } from "./api";
import { computeNotification, type UnreadSnapshot } from "./notification";

const POLL_MS = 30_000;

type UnreadState = { unreadThreads: number; needsReply: number };

const UnreadContext = createContext<UnreadState>({ unreadThreads: 0, needsReply: 0 });

export function useUnread(): UnreadState {
  return useContext(UnreadContext);
}

function notifyBridge(title: string, body: string): void {
  const bridge = window.omBridge;
  if (bridge?.notifyNewMessages) {
    void bridge.notifyNewMessages(title, body);
    return;
  }
  // Fallback web (dev nel browser senza Electron)
  if (typeof Notification === "undefined") return;
  if (Notification.permission === "granted") {
    new Notification(title, { body });
  } else if (Notification.permission === "default") {
    // La richiesta del permesso è ASINCRONA: senza attenderne l'esito questa
    // notifica sarebbe persa per sempre — il chiamante avanza comunque
    // `prevRef` e `computeNotification` non ripropone mai lo stesso
    // `latest_inbound_at`, quindi il primo messaggio non tornerebbe più.
    void Notification.requestPermission().then((permission) => {
      if (permission === "granted") new Notification(title, { body });
    });
  }
}

/**
 * Provider globale per i non-letti dei Messaggi.
 *
 * - Polla `GET /api/mail/unread-count` ogni 30s e al `window focus`.
 * - Espone `{ unreadThreads, needsReply }` via `useUnread()`.
 * - Quando `latest_inbound_at` avanza → invia notifica Windows via bridge.
 * - Errori silenziosamente ignorati (spec §2: mai toast/banner ricorrenti).
 * - Se la finestra è a fuoco E la route è /messaggi → notifica soppressa,
 *   badge aggiornato comunque.
 */
export function UnreadProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<UnreadState>({ unreadThreads: 0, needsReply: 0 });
  const prevRef = useRef<UnreadSnapshot | null>(null);
  const location = useLocation();
  const locationRef = useRef(location.pathname);
  locationRef.current = location.pathname;

  useEffect(() => {
    let cancelled = false;

    async function poll(): Promise<void> {
      try {
        const snap = await fetchUnreadCount();
        if (cancelled) return;

        setState({ unreadThreads: snap.unread_threads, needsReply: snap.needs_reply });

        const payload = computeNotification(prevRef.current, snap);
        const onMessaggiFocused =
          locationRef.current.startsWith("/messaggi") && document.hasFocus();
        if (payload && !onMessaggiFocused) {
          notifyBridge(payload.title, payload.body);
        }

        prevRef.current = snap;
      } catch {
        // Poll best-effort: errore silenzioso (spec §2).
        // Il badge resta invariato all'ultimo valore noto.
      }
    }

    // ``setTimeout`` a catena, non ``setInterval``: il timer riparte DOPO la
    // risposta. Misurato in produzione il 2026-09-08: con l'``setInterval``
    // questo endpoint rispondeva in 52 s mentre il timer ripartiva ogni 30 —
    // ogni giro ne lasciava indietro uno, e le richieste appese tenevano
    // occupate le connessioni del pool (20+10) finche' non si esauriva. Sono
    // 8,27 richieste sempre in volo da tre soli browser. Stesso schema gia'
    // usato da SyncStatusPill e HaltedLanesAlert.
    let timerId: number | null = null;
    const riavvia = (): void => {
      if (cancelled) return;
      timerId = window.setTimeout(() => void giro(), POLL_MS);
    };
    async function giro(): Promise<void> {
      await poll();
      riavvia();
    }

    void giro();
    const onFocus = (): void => void poll();
    window.addEventListener("focus", onFocus);

    return () => {
      cancelled = true;
      if (timerId !== null) window.clearTimeout(timerId);
      window.removeEventListener("focus", onFocus);
    };
  }, []);

  return <UnreadContext.Provider value={state}>{children}</UnreadContext.Provider>;
}
